import authApi from "./authApi";
const MEMBERS_URL = "/members";
const MASTERS_URL = "/masters";

export const MASTER_ENDPOINTS = {
    profile_created_by: "profile-created-by",
    gender: "gender",
    designation: "designation",
    family_type: "family-type",
    height: "height",
    weight: "weight",
    blood_group: "blood-group",
    color: "color",
    education: "education",
    occupation: "occupations",
    dhosam: "dhosam",
    nakshatram: "nakshatram",
    rasi: "rasi",
    paadam: "paadam",
    marital_status: "marital-status",
    state: "state",
    district: "district",
    city: "city",
    family_koottam: "family-koottam",
    family_temple: "family-temple",
    family_temple_place: "family-temple-place",
};

const unwrap = (response) => response?.data;
const checkResponse = (data) => {
    if (data?.success === false) {
        throw new Error(data.message || "The API request failed.");
    }
    return data;
};

const validateRfId = (rfId) => {
    if (
        rfId === undefined ||
        rfId === null ||
        String(rfId).trim() === ""
    ) {
        throw new Error("Member rfId is required.");
    }
    return encodeURIComponent(String(rfId));
};

const getErrorMessage = (error) => {
    return (
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong. Please try again."
    );
};

export const getMembers = async (params = {}) => {
    try {
        const response = await authApi.get(MEMBERS_URL, {
            params,
        });
        return checkResponse(unwrap(response));
    } catch (error) {
        throw new Error(getErrorMessage(error));
    }
};

export const getMemberById = async (rfId) => {
    try {
        const id = validateRfId(rfId);
        const response = await authApi.get(
            `${MEMBERS_URL}/${id}`
        );
        return checkResponse(unwrap(response));
    } catch (error) {
        throw new Error(getErrorMessage(error));
    }
};

export const createMember = async (payload) => {
    try {
        const response = await authApi.post(MEMBERS_URL, payload);
        return checkResponse(unwrap(response));
    } catch (error) {
        throw new Error(getErrorMessage(error));
    }
};

export const updateMember = async (rfId, payload) => {
    try {
        const id = validateRfId(rfId);
        const response = await authApi.put(`${MEMBERS_URL}/${id}`, payload);
        return checkResponse(unwrap(response));
    } catch (error) {
        throw new Error(getErrorMessage(error));
    }
};

export const updateMemberStatus = async (rfId, status) => {
    try {
        const id = validateRfId(rfId);
        const normalizedStatus = String(status).toLowerCase();
        if (!["active", "inactive"].includes(normalizedStatus)) {
            throw new Error(
                "Status must be active or inactive."
            );
        }
        const response = await authApi.patch(`${MEMBERS_URL}/${id}/status`,
            {
                status: normalizedStatus,
            }
        );
        return checkResponse(unwrap(response));
    } catch (error) {
        throw new Error(getErrorMessage(error));
    }
};

export const approveMember = async (rfId, approvedBy) => {
    try {
        const id = validateRfId(rfId);
        const payload = {};
        if (approvedBy !== undefined && approvedBy !== null) {
            payload.approved_by = approvedBy;
        }
        const response = await authApi.patch(`${MEMBERS_URL}/${id}/approve`, payload);
        return checkResponse(unwrap(response));
    } catch (error) {
        throw new Error(getErrorMessage(error));
    }
};

export const rejectMember = async (rfId, reason, rejectedBy) => {
    try {
        const id = validateRfId(rfId);
        if (!reason || !String(reason).trim()) {
            throw new Error("Rejection reason is required.");
        }
        const payload = {
            reason: String(reason).trim(),
        };
        if (rejectedBy !== undefined && rejectedBy !== null) {
            payload.rejected_by = rejectedBy;
        }
        const response = await authApi.patch(`${MEMBERS_URL}/${id}/reject`, payload);
        return checkResponse(unwrap(response));
    } catch (error) {
        throw new Error(getErrorMessage(error));
    }
};

export const DOCUMENT_TYPES = {
    PROFILE_PICTURE: "profile_picture",
    COMMUNITY_CERTIFICATE: "community_certificate",
    HOROSCOPE_COPY: "horoscope_copy",
    DIVORCE_CERTIFICATE: "divorce_certificate",
    OTHER: "other",
};

export const uploadMemberDocument = async (rfId, file, documentType) => {
    try {
        const id = validateRfId(rfId);
        if (!(file instanceof File)) {
            throw new Error("Please select a valid document file.");
        }
        const formData = new FormData();
        formData.append("document", file, file.name);
        formData.append("document_type", documentType);
        const response = await authApi.post(`${MEMBERS_URL}/${id}/documents`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );
        return checkResponse(unwrap(response));
    } catch (error) {
        throw new Error(getErrorMessage(error));
    }
};

export const getMemberDocuments = async (rfId) => {
    try {
        const id = validateRfId(rfId);
        const response = await authApi.get(`${MEMBERS_URL}/${id}/documents`);
        return checkResponse(unwrap(response));
    } catch (error) {
        throw new Error(getErrorMessage(error));
    }
};

export const getMasterList = async (masterName, params = {}) => {
    try {
        const endpoint = MASTER_ENDPOINTS[masterName];
        if (!endpoint) {
            throw new Error(
                `Unknown master: ${masterName}`
            );
        }
        const response = await authApi.get(
            `${MASTERS_URL}/${endpoint}`,
            {
                params,
            }
        );
        const result = checkResponse(unwrap(response));
        if (Array.isArray(result)) {
            return result;
        }
        if (Array.isArray(result?.data)) {
            return result.data;
        }
        if (Array.isArray(result?.items)) {
            return result.items;
        }
        return [];
    } catch (error) {
        throw new Error(getErrorMessage(error));
    }
};

export const getAllMemberMasters = async () => {
    const entries = Object.keys(MASTER_ENDPOINTS);
    const results = await Promise.all(
        entries.map(async (key) => {
            try {
                const data = await getMasterList(key);
                return [key, data];
            } catch (error) {
                return [key, []];
            }
        })
    );
    return Object.fromEntries(results);
};

export const deleteMemberDocument = async (rfId, documentId) => {
    try {
        const response = await authApi.delete(
            `${MEMBERS_URL}/${rfId}/documents/${documentId}`
        );
        return checkResponse(unwrap(response));
    } catch (error) {
        throw new Error(getErrorMessage(error));
    }
};

const membersAPi = { getMembers, getMemberById, createMember, updateMember, updateMemberStatus, approveMember, 
    rejectMember, uploadMemberDocument, getMemberDocuments, getMasterList, getAllMemberMasters, deleteMemberDocument, MASTER_ENDPOINTS, DOCUMENT_TYPES, };

export default membersAPi;